import { toast } from '@eleo-rh/core-ui/contexts'
import axios, { type AxiosResponse } from 'axios'
import { IMAGE_BLANK_DATA } from 'constants/image'
import { useI18n } from 'context/I18nProvider'
import { messageFromError } from 'context/NotificationProvider'
import { useQuery } from 'context/QueryProvider'
import { useCallback, useEffect, useRef, useState } from 'react'
import { printBlob as printBlobData, triggerDownloadBlob } from 'utils/binary'
import { isStatelessResource } from 'utils/environment'
import { getFilenameFromContentDisposition } from 'utils/headers'

export function useBlob() {
  const { client } = useQuery()
  const { i18n } = useI18n()

  const fetchBlobResponse = useCallback(
    async (url: string, headers?: Record<string, string> | null, options: Record<string, unknown> = {}) => {
      let response: AxiosResponse<Blob>
      try {
        response = await client.request(
          url,
          {
            plainResponse: true,
            responseType: 'blob',
            validateStatus: (status: number) => status >= 200 && status < 500,
            ...options,
          },
          headers,
        )
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
          const blob = error.response.data
          try {
            error.response.data = JSON.parse(await blob.text())
          } catch (_e) {
            error.response.data = blob
          }
        }
        throw error
      }

      if (response && response.status >= 400 && response.status < 500) {
        if (response.data instanceof Blob) {
          try {
            const decodedData = await response.data.text()
            toast.error({
              title: i18n('label.error'),
              description: i18n(
                messageFromError({ response: { data: JSON.parse(decodedData), status: response.status } }),
              ),
            })
          } catch (_e) {
            toast.error({ title: i18n('label.error'), description: i18n(messageFromError()) })
          }
          return null
        }
        toast.error({ title: i18n('label.error'), description: i18n(messageFromError(response)) })
        return null
      }

      return response
    },
    [client, i18n],
  )

  const downloadBlob = useCallback(
    async (
      url: string,
      target?: string,
      headers?: Record<string, string> | null,
      options: Record<string, unknown> = {},
    ) => {
      const response = await fetchBlobResponse(url, headers, options)
      if (!response?.data) return !!response

      triggerDownloadBlob(
        response.data,
        response.headers['content-type'] || 'application/octet-stream',
        getFilenameFromContentDisposition(response.headers['content-disposition']),
        target,
      )

      return true
    },
    [fetchBlobResponse],
  )

  const printBlob = useCallback(
    async (url: string, headers?: Record<string, string> | null, options: Record<string, unknown> = {}) => {
      const response = await fetchBlobResponse(url, headers, options)
      if (!response?.data) return !!response

      printBlobData(response.data, response.headers['content-type'] || 'application/octet-stream')

      return true
    },
    [fetchBlobResponse],
  )

  return {
    fetchBlobResponse,
    downloadBlob,
    printBlob,
  }
}

export const useResourceUrl = (url: string | null | undefined, blankData: string | null = null) => {
  const { client } = useQuery()

  const [resourceUrl, setResourceUrl] = useState<string | null>(blankData)
  const lastAxiosCancelSource = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null)
  const lastObjectUrl = useRef<string | null>(null)

  useEffect(() => {
    async function getImageBlob() {
      if (url) {
        if (isStatelessResource(url)) {
          if (lastAxiosCancelSource.current) {
            lastAxiosCancelSource.current.cancel()
          }
          const source = axios.CancelToken.source()
          lastAxiosCancelSource.current = source

          const response = await client.request(url, {
            responseType: 'arraybuffer',
            cancelToken: source.token,
            plainResponse: true,
          })

          const file = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/octet-stream',
          })

          const fileUrl = window.URL.createObjectURL(file)

          if (lastObjectUrl.current) {
            // Free memory
            window.URL.revokeObjectURL(lastObjectUrl.current)
          }
          lastObjectUrl.current = fileUrl
          setResourceUrl(fileUrl)
        } else {
          setResourceUrl(url)
        }
      } else {
        // Put back initial data in case of removed url
        setResourceUrl(blankData)
      }
    }

    getImageBlob()

    return function cleanup() {
      // Cancel any fetch request
      if (lastAxiosCancelSource.current) {
        lastAxiosCancelSource.current.cancel()
      }

      if (lastObjectUrl.current) {
        // Free memory on unmount
        window.URL.revokeObjectURL(lastObjectUrl.current)
      }
    }
  }, [client, url, blankData])

  return [resourceUrl, setResourceUrl] as const
}

export const useImageUrl = (url: string | null | undefined) => useResourceUrl(url, IMAGE_BLANK_DATA)
